import React, { useRef, useState } from 'react';
import { Box, Popover, Typography, Chip, Divider, Button, useTheme } from '@mui/material';
import {
  MyLocation,
  EmojiEvents,
  Today,
  LocalOffer,
  AccessTime,
  DirectionsCar,
  Tune,
} from '@mui/icons-material';
import FilterPill from './shared/FilterPill';

function LibraryFilterPills({
  filters,
  setFilters,
  locationLoading,
  onNearMeClick,
  onTurnOffNearMe,
  onOpenMore,
  moreBadgeCount,
  clearUrlParams,
}) {
  const theme = useTheme();

  const nearMeRef = useRef(null);
  const offersRef = useRef(null);
  const [nearMeAnchor, setNearMeAnchor] = useState(null);
  const [offersAnchor, setOffersAnchor] = useState(null);

  const handleNearMePopoverOpen = () => setNearMeAnchor(nearMeRef.current);
  const handleOffersPopoverOpen = () => setOffersAnchor(offersRef.current);

  const toggleAmenity = (amenity) => {
    setFilters((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  const handleNearMeClick = () => {
    if (filters.useLocation) {
      handleNearMePopoverOpen();
    } else {
      onNearMeClick();
    }
  };

  const handleOffersClick = () => {
    if (filters.minDiscount) {
      handleOffersPopoverOpen();
    } else {
      setFilters((prev) => ({ ...prev, minDiscount: '10' }));
    }
  };

  const nearMeLabel = filters.useLocation
    ? `Near Me · ${filters.nearbyRadius} KM`
    : 'Near Me';

  const offersLabel = filters.minDiscount
    ? `Offers · ${filters.minDiscount}%+`
    : 'Offers';

  const moreLabel = moreBadgeCount > 0 ? `More · ${moreBadgeCount}` : 'More';

  const popoverSlotProps = {
    paper: { sx: { mt: 1, p: 2, borderRadius: 3, minWidth: 220 } },
  };

  return (
    <Box
      component="nav"
      role="region"
      aria-label="Quick filters"
      sx={{ position: 'relative', mt: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          flexWrap: 'nowrap',
          px: 0,
          pb: 0.5,
        }}
      >
        {/* 1 — Near Me */}
        <Box ref={nearMeRef} sx={{ flexShrink: 0 }}>
          <FilterPill
            label={nearMeLabel}
            icon={<MyLocation fontSize="small" />}
            active={filters.useLocation}
            loading={locationLoading}
            hasPopover
            onClick={handleNearMeClick}
          />
        </Box>

        {/* 2 — Top Rated */}
        <FilterPill
          label="Top Rated"
          icon={<EmojiEvents fontSize="small" />}
          active={filters.topRated}
          onClick={() => {
            const next = !filters.topRated;
            setFilters((prev) => ({ ...prev, topRated: next }));
            if (!next) clearUrlParams();
          }}
        />

        {/* 3 — Day Pass */}
        <FilterPill
          label="Day Pass"
          icon={<Today fontSize="small" />}
          active={filters.oneDayPass}
          onClick={() => {
            const next = !filters.oneDayPass;
            setFilters((prev) => ({ ...prev, oneDayPass: next }));
            if (!next) clearUrlParams();
          }}
        />

        {/* 4 — Offers */}
        <Box ref={offersRef} sx={{ flexShrink: 0 }}>
          <FilterPill
            label={offersLabel}
            icon={<LocalOffer fontSize="small" />}
            active={!!filters.minDiscount}
            hasPopover
            onClick={handleOffersClick}
          />
        </Box>

        {/* 5 — 24/7 Access */}
        <FilterPill
          label="24/7"
          icon={<AccessTime fontSize="small" />}
          active={filters.amenities.includes('24/7 Access')}
          onClick={() => toggleAmenity('24/7 Access')}
        />

        {/* 6 — Parking */}
        <FilterPill
          label="Parking"
          icon={<DirectionsCar fontSize="small" />}
          active={filters.amenities.includes('Parking')}
          onClick={() => toggleAmenity('Parking')}
        />

        {/* 7 — More filters */}
        <FilterPill
          label={moreLabel}
          icon={<Tune fontSize="small" />}
          active={false}
          onClick={onOpenMore}
          ariaLabel={`More filters${moreBadgeCount ? `, ${moreBadgeCount} active` : ''}`}
        />
      </Box>

      {/* Right-edge fade hint */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: 32,
          height: '100%',
          pointerEvents: 'none',
          background: `linear-gradient(to right, transparent, ${theme.palette.background.paper})`,
        }}
      />

      {/* Near Me popover */}
      <Popover
        open={Boolean(nearMeAnchor)}
        anchorEl={nearMeAnchor}
        onClose={() => setNearMeAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={popoverSlotProps}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
          Search radius
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
          {[2, 5, 10, 15].map((km) => (
            <Chip
              key={km}
              label={`${km} KM`}
              color={filters.nearbyRadius === km ? 'primary' : 'default'}
              variant={filters.nearbyRadius === km ? 'filled' : 'outlined'}
              onClick={() => setFilters((prev) => ({ ...prev, nearbyRadius: km }))}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Button
          size="small"
          color="error"
          sx={{ textTransform: 'none' }}
          onClick={() => {
            onTurnOffNearMe();
            setNearMeAnchor(null);
            clearUrlParams();
          }}
        >
          Turn off Near Me
        </Button>
      </Popover>

      {/* Offers popover */}
      <Popover
        open={Boolean(offersAnchor)}
        anchorEl={offersAnchor}
        onClose={() => setOffersAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={popoverSlotProps}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
          Minimum discount
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
          {[10, 20, 30, 40].map((pct) => (
            <Chip
              key={pct}
              label={`${pct}%`}
              color={filters.minDiscount === String(pct) ? 'primary' : 'default'}
              variant={filters.minDiscount === String(pct) ? 'filled' : 'outlined'}
              onClick={() => setFilters((prev) => ({ ...prev, minDiscount: String(pct) }))}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Button
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={() => {
            setFilters((prev) => ({ ...prev, minDiscount: '' }));
            setOffersAnchor(null);
            clearUrlParams();
          }}
        >
          Off
        </Button>
      </Popover>
    </Box>
  );
}

export default LibraryFilterPills;
